<script setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import SgpAPI from 'dashboard/api/sgp';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { normalizeBrazilPhone } from 'dashboard/helpers/phoneBrazil';

const props = defineProps({
  context: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { t: $t } = useI18n();

const loadingAux = ref(false);
const submitting = ref(false);
const submitResult = ref(null);

const loadingCep = ref(false);
const cepError = ref('');
const geocodeLoading = ref(false);
const geocodeError = ref('');
const addressAutocompleteLoading = ref(false);
const addressAutocompleteResults = ref([]);
const addressAutocompleteOpen = ref(false);
const addressAutocompleteFocused = ref(false);
let addressAutocompleteTimer = null;

const type = ref('');
const showAdvanced = ref(false);
const showContract = ref(false);
const step = ref(0);

const plans = ref([]);
const dueDates = ref([]);
const vendors = ref([]);
const planOpen = ref(false);
const dueOpen = ref(false);
const vendorOpen = ref(false);

const lastCepSearched = ref('');
const numberInputRef = ref(null);
const selectedCtoIndex = ref(null);

const errors = ref({});

const form = ref({
  // identificação / pessoa
  nome: '',
  nomefantasia: '',
  respempresa: '',
  respcpf: '',
  cpfcnpj: '',
  datanasc: '',
  rg: '',
  rg_emissor: '',
  nomepai: '',
  nomemae: '',
  nacionalidade: '',
  naturalidade: '',
  estadocivil: '',
  sexo: '',
  profissao: '',
  observacao: '',
  // contato
  email: '',
  celular: '',
  // endereço
  logradouro: '',
  numero: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
  complemento: '',
  pontoreferencia: '',
  condominio: '',
  map_ll: '',
  // contrato / serviço (opcional)
  portador_id: '',
  pop_id: '',
  nas_id: '',
  plano_id: '',
  planointernet_id: '',
  planobase_id: '',
  vencimento_id: '',
  login: '',
  senha: '',
  central_senha: '',
  modoaquisicao: '',
  fidelidade_id: '',
  contrato_id: '',
  ip: '',
  mac: '',
  splitter_id: '',
  splitter_port: '',
  servicodesc: '',
  tipo_equipamento_id: '',
  midia_id: '',
  vendedor_id: '',
  tecnico_id: '',
  os_instalacao: '',
  instalacao_quantidade_parcelas: '',
  instalacao_preco: '',
  instalacao_desconto: '',
  instalacao_entrada: '',
  instalacao_entrada_forma: '',
  instalacao_parcela_forma: '',
  ippool_id: '',
  mac_dhcp: '',
  comissao_tipo: '',
  comissao_valor: '',
  comissao_qtd_parcelas: '',
  usuariocad_id: '',
  formacobranca_id: '',
  precadastro_ativar: '',
});

const isPF = computed(() => type.value === 'PF');
const isPJ = computed(() => type.value === 'PJ');

const steps = computed(() => [
  {
    key: 'type',
    title: $t('SGP.PRE.STEP_TYPE'),
    subtitle: $t('SGP.PRE.STEP_TYPE_DESC'),
  },
  {
    key: 'person',
    title: $t('SGP.PRE.STEP_PERSON'),
    subtitle: $t('SGP.PRE.STEP_PERSON'),
  },
  {
    key: 'address',
    title: $t('SGP.PRE.ADDRESS_SECTION'),
    subtitle: $t('SGP.PRE.ADDRESS_SECTION'),
  },
  {
    key: 'plan',
    title: $t('SGP.PRE.CONTRACT_SECTION'),
    subtitle: $t('SGP.PRE.CONTRACT_SECTION'),
  },
  {
    key: 'review',
    title: $t('SGP.PRE.STEP_REVIEW'),
    subtitle: $t('SGP.PRE.STEP_REVIEW'),
  },
]);

const stepRequiredMap = computed(() => ({
  type: ['type'],
  person: ['nome', 'cpfcnpj', 'email', 'celular'],
  address: ['logradouro', 'numero', 'bairro', 'cidade', 'uf', 'cep'],
  plan: ['plano_id', 'vencimento_id'],
  review: [],
}));

const allRequired = computed(() =>
  Array.from(
    new Set(Object.values(stepRequiredMap.value).flatMap(items => items))
  )
);

function markError(field, message) {
  errors.value = { ...errors.value, [field]: message };
}

function clearError(field) {
  if (!errors.value[field]) return;
  const clone = { ...errors.value };
  delete clone[field];
  errors.value = clone;
}

function isValidCpf(value) {
  if (!value || value.length !== 11 || /^(\d)\1+$/.test(value)) return false;
  const calc = length => {
    const numbers = value.slice(0, length);
    const weights = numbers
      .split('')
      .map((num, idx) => Number(num) * (length + 1 - idx));
    const sum = weights.reduce((acc, n) => acc + n, 0);
    const mod = (sum * 10) % 11;
    return mod === 10 ? 0 : mod;
  };
  const d1 = calc(9);
  const d2 = calc(10);
  return d1 === Number(value[9]) && d2 === Number(value[10]);
}

function isValidCnpj(value) {
  if (!value || value.length !== 14 || /^(\d)\1+$/.test(value)) return false;
  const calc = length => {
    const slice = value.slice(0, length);
    const factors =
      length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = slice
      .split('')
      .reduce((acc, num, idx) => acc + Number(num) * factors[idx], 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const d1 = calc(12);
  const d2 = calc(13);
  return d1 === Number(value[12]) && d2 === Number(value[13]);
}

function validateDocumentField(force = false) {
  const digits = (form.value.cpfcnpj || '').replace(/\D+/g, '');
  if (!digits) {
    if (force) {
      markError('cpfcnpj', $t('SGP.ERRORS.REQUIRED'));
    } else {
      clearError('cpfcnpj');
    }
    return;
  }
  const expectedLength = isPF.value ? 11 : 14;
  if (digits.length < expectedLength) {
    if (force) {
      markError(
        'cpfcnpj',
        $t('SGP.ERRORS.INVALID_DOCUMENT') || $t('SGP.ERRORS.INVALID_DATA')
      );
    } else {
      clearError('cpfcnpj');
    }
    return;
  }
  if (digits.length > expectedLength) {
    markError(
      'cpfcnpj',
      $t('SGP.ERRORS.INVALID_DOCUMENT') || $t('SGP.ERRORS.INVALID_DATA')
    );
    return;
  }

  const valid = isPF.value ? isValidCpf(digits) : isValidCnpj(digits);
  if (valid) {
    clearError('cpfcnpj');
  } else {
    markError(
      'cpfcnpj',
      $t('SGP.ERRORS.INVALID_DOCUMENT') || $t('SGP.ERRORS.INVALID_DATA')
    );
  }
}

function valueForKey(key) {
  if (key === 'type') return type.value;
  return form.value[key];
}

function validateKeys(keys) {
  errors.value = {};
  keys.forEach(key => {
    const value = valueForKey(key);
    if (!value || String(value).trim() === '') {
      markError(key, $t('SGP.ERRORS.REQUIRED'));
    }
  });
  if (keys.includes('cpfcnpj')) {
    validateDocumentField(true);
  }
  if (form.value.map_ll?.toString().trim()) {
    const ll = (form.value.map_ll || '').split(',');
    const hasTwo =
      ll.length === 2 && ll.every(part => !Number.isNaN(Number(part.trim())));
    if (!hasTwo) {
      markError('map_ll', $t('SGP.ERRORS.INVALID_DATA'));
    }
  }
  return Object.keys(errors.value).length === 0;
}

function stepKeys(stepKey) {
  return stepRequiredMap.value[stepKey] || [];
}

function goNext() {
  const current = steps.value[step.value]?.key;
  const keys = stepKeys(current);
  if (!validateKeys(keys)) return;
  step.value = Math.min(step.value + 1, steps.value.length - 1);
}

function goPrev() {
  if (step.value === 0) return;
  step.value -= 1;
}

function resetFieldsForType(newType) {
  if (newType === 'PF') {
    form.value.nomefantasia = '';
    form.value.respempresa = '';
    form.value.respcpf = '';
  }
}

function setTypeSelection(newType) {
  type.value = newType;
  clearError('type');
  resetFieldsForType(newType);
  validateDocumentField(false);
  if (step.value === 0) {
    step.value = 1;
  }
}

const stepStatus = index => {
  if (index < step.value) return 'done';
  if (index === step.value) return 'current';
  return 'pending';
};

async function loadAux() {
  try {
    loadingAux.value = true;
    const [plansRes, dueRes, vendorRes] = await Promise.all([
      SgpAPI.listPrePlans(),
      SgpAPI.listPreDueDates(),
      SgpAPI.listPreVendors(),
    ]);
    plans.value = plansRes.data || [];
    dueDates.value = dueRes.data || [];
    vendors.value = vendorRes.data || [];
  } catch (e) {
    plans.value = [];
    dueDates.value = [];
    vendors.value = [];
  } finally {
    loadingAux.value = false;
  }
}

function fillFromContext() {
  const payload = props.context?.payload || {};
  if (!payload || Object.keys(payload).length === 0) return;
  // Dados pessoais (bridge a partir de cliente identificado)
  form.value.nome = payload.nome || form.value.nome;
  form.value.cpfcnpj = payload.cpfcnpj || form.value.cpfcnpj;
  form.value.email = payload.email || form.value.email;
  form.value.celular =
    payload.telefone || payload.celular || form.value.celular;
  // Endereço
  form.value.logradouro =
    payload.endereco || payload.logradouro || form.value.logradouro;
  form.value.numero = payload.numero || form.value.numero;
  form.value.bairro = payload.bairro || form.value.bairro;
  form.value.cep = payload.cep || form.value.cep;
  form.value.map_ll = payload.coordenada || payload.map_ll || form.value.map_ll;
  form.value.cidade = payload.cidade || form.value.cidade;
  form.value.uf = payload.uf || form.value.uf;
  form.value.complemento = payload.complemento || form.value.complemento;
  // Infra (pré-preenche POP do cliente)
  form.value.pop_id = payload.pop_id || form.value.pop_id;
}

function selectCto(cto, index) {
  selectedCtoIndex.value = index;
  if (cto?.lat && cto?.lng) {
    form.value.map_ll = `${cto.lat},${cto.lng}`;
  }
  if (cto?.splitter_id) form.value.splitter_id = cto.splitter_id;
  const port =
    cto?.splitter_port || cto?.port || cto?.porta || cto?.pon || cto?.slot;
  if (port) form.value.splitter_port = port;
  // Se a CTO trouxer pop vinculado, preenche
  const popId = cto?.pop_id || cto?.popId || cto?.pop;
  if (popId && !form.value.pop_id) form.value.pop_id = popId;
}

function closeAddressAutocomplete() {
  addressAutocompleteOpen.value = false;
}

function canUseAddressAutocomplete() {
  const cleanCep = (form.value.cep || '').replace(/\D+/g, '');
  return cleanCep.length !== 8;
}

function handleAddressFocus() {
  addressAutocompleteFocused.value = true;
  addressAutocompleteOpen.value =
    canUseAddressAutocomplete() && addressAutocompleteResults.value.length > 0;
}

function handleAddressBlur() {
  addressAutocompleteFocused.value = false;
  setTimeout(() => {
    closeAddressAutocomplete();
  }, 150);
}

function selectAddressSuggestion(item) {
  if (!item) return;
  form.value.logradouro = item.logradouro || form.value.logradouro;
  if (!form.value.bairro && item.bairro) form.value.bairro = item.bairro;
  if (!form.value.cidade && item.localidade) {
    form.value.cidade = item.localidade;
  }
  if (!form.value.uf && item.uf) form.value.uf = item.uf;
  if (!form.value.cep && item.cep) form.value.cep = item.cep;
  addressAutocompleteOpen.value = false;
}

function extractGeo(result) {
  if (!result || typeof result !== 'object') return null;
  const geo = result.geo || result.location || {};
  const lat =
    geo.lat ?? geo.latitude ?? result.lat ?? result.latitude ?? geo.lat;
  const lon =
    geo.lon ??
    geo.lng ??
    geo.longitude ??
    result.lon ??
    result.lng ??
    result.longitude;

  if (lat === undefined || lat === null || lat === '') return null;
  if (lon === undefined || lon === null || lon === '') return null;

  return { lat: lat.toString(), lon: lon.toString() };
}

const canManualGeocode = computed(() => {
  const logradouro = (form.value.logradouro || '').trim();
  const numero = (form.value.numero || '').toString().trim();
  return Boolean(logradouro && numero);
});

async function fetchAddressAutocomplete(query) {
  try {
    addressAutocompleteLoading.value = true;
    const { data } = await SgpAPI.cepAutocomplete({
      q: query,
      uf: form.value.uf,
      localidade: form.value.cidade,
      bairro: form.value.bairro,
      limit: 8,
    });
    addressAutocompleteResults.value = data?.results || [];
    addressAutocompleteOpen.value =
      addressAutocompleteFocused.value &&
      canUseAddressAutocomplete() &&
      addressAutocompleteResults.value.length > 0;
  } catch (e) {
    addressAutocompleteResults.value = [];
    addressAutocompleteOpen.value = false;
  } finally {
    addressAutocompleteLoading.value = false;
  }
}

async function fetchViaCep(cleanCep) {
  const normalized = (cleanCep || '').replace(/\D+/g, '');
  if (normalized.length !== 8) {
    cepError.value = '';
    return;
  }
  try {
    loadingCep.value = true;
    cepError.value = '';
    const { data } = await SgpAPI.cepLookup(normalized);
    const result = data?.primary || data?.results?.[0];
    if (!result) {
      cepError.value = $t('SGP.PRE.CEP_NOT_FOUND');
      return;
    }
    form.value.logradouro = result.logradouro || form.value.logradouro;
    form.value.bairro = result.bairro || form.value.bairro;
    form.value.cidade = result.localidade || form.value.cidade;
    form.value.uf = result.uf || form.value.uf;
    form.value.cep = result.cep || normalized;
    addressAutocompleteResults.value = [];
    addressAutocompleteOpen.value = false;
    lastCepSearched.value = normalized;

    await nextTick();
    numberInputRef.value?.focus?.();
  } catch (e) {
    cepError.value =
      e.response?.status === 404
        ? $t('SGP.PRE.CEP_NOT_FOUND')
        : $t('SGP.PRE.CEP_ERROR');
  } finally {
    loadingCep.value = false;
  }
}

async function geocodeFromAddress() {
  const logradouro = (form.value.logradouro || '').trim();
  if (!logradouro || logradouro.length < 2) {
    geocodeError.value = $t('SGP.PRE.GEO_MISSING_ADDRESS');
    return;
  }
  const numero = (form.value.numero || '').toString().trim();
  if (!numero) {
    geocodeError.value = $t('SGP.PRE.GEO_MISSING_NUMBER');
    return;
  }
  geocodeLoading.value = true;
  geocodeError.value = '';
  try {
    const params = {
      logradouro,
      numero: form.value.numero,
      bairro: form.value.bairro,
      localidade: form.value.cidade,
      uf: form.value.uf,
      geo: true,
      limit: 1,
      offset: 0,
    };
    if (!numero && params.uf && params.localidade) params.strict = true;

    const { data } = await SgpAPI.cepSearch(params);
    const result = data?.results?.[0];
    const geo = extractGeo(result);
    if (geo) {
      form.value.map_ll = `${geo.lat},${geo.lon}`;
      return;
    }

    geocodeError.value = $t('SGP.PRE.GEO_NOT_FOUND');
  } catch (e) {
    geocodeError.value = e.response?.data?.error || $t('SGP.PRE.GEO_ERROR');
  } finally {
    geocodeLoading.value = false;
  }
}

function cleanPayload(raw) {
  const cleaned = {};
  Object.entries(raw).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'string' && value.trim() === '') return;
    cleaned[key] = typeof value === 'string' ? value.trim() : value;
  });
  return cleaned;
}

const selectedPlanLabel = computed(() => {
  const selected = plans.value.find(
    plan => String(plan.id) === String(form.value.plano_id)
  );
  return selected?.descricao || $t('SGP.PRE.SELECT_OPTION');
});

const selectedDueLabel = computed(() => {
  const selected = dueDates.value.find(
    due => String(due.id) === String(form.value.vencimento_id)
  );
  return (
    selected?.vencimento ||
    selected?.dia ||
    selected?.id ||
    $t('SGP.PRE.SELECT_OPTION')
  );
});

const selectedVendorLabel = computed(() => {
  const selected = vendors.value.find(
    vendor => String(vendor.id) === String(form.value.vendedor_id)
  );
  return selected?.nome || $t('SGP.PRE.SELECT_OPTION');
});

function firstErrorStep() {
  const keys = Object.keys(errors.value);
  if (keys.length === 0) return 0;
  const field = keys[0];
  const idx = steps.value.findIndex(stepItem =>
    (stepRequiredMap.value[stepItem.key] || []).includes(field)
  );
  return idx >= 0 ? idx : 0;
}

async function submit() {
  submitting.value = true;
  submitResult.value = null;
  if (!validateKeys(allRequired.value)) {
    step.value = firstErrorStep();
    submitting.value = false;
    submitResult.value = {
      error: true,
      message: $t('SGP.ERRORS.INVALID_DATA'),
    };
    return;
  }
  try {
    const payload = { ...form.value };
    const docDigits = (payload.cpfcnpj || '').replace(/\D+/g, '');
    payload.cep = (payload.cep || '').replace(/\D+/g, '');
    payload.celular = normalizeBrazilPhone(payload.celular);
    payload.precadastro_ativar = payload.precadastro_ativar ? 1 : '';
    payload.cpfcnpj = docDigits;
    if (isPF.value) {
      delete payload.respcpf;
      delete payload.respempresa;
    } else if (payload.respcpf)
      payload.respcpf = (payload.respcpf || '').replace(/\D+/g, '');
    delete payload.cpf;
    delete payload.cnpj;

    const sender = isPF.value ? SgpAPI.createPrePF : SgpAPI.createPrePJ;
    const cleaned = cleanPayload(payload);
    const { data } = await sender.call(SgpAPI, cleaned);
    submitResult.value = data;
  } catch (e) {
    submitResult.value = {
      error: true,
      message: e.response?.data?.error || e.message,
    };
  } finally {
    submitting.value = false;
  }
}

watch(
  () => form.value.logradouro,
  newVal => {
    const query = (newVal || '').trim();
    if (!addressAutocompleteFocused.value || !canUseAddressAutocomplete()) {
      addressAutocompleteResults.value = [];
      addressAutocompleteOpen.value = false;
      return;
    }
    if (query.length < 4) {
      addressAutocompleteResults.value = [];
      addressAutocompleteOpen.value = false;
      return;
    }
    if (addressAutocompleteTimer) clearTimeout(addressAutocompleteTimer);
    addressAutocompleteTimer = setTimeout(() => {
      fetchAddressAutocomplete(query);
    }, 400);
  }
);

watch(
  () => form.value.cep,
  newVal => {
    const clean = (newVal || '').replace(/\D+/g, '');
    if (clean.length === 8) {
      addressAutocompleteResults.value = [];
      addressAutocompleteOpen.value = false;
    }
    if (clean.length === 8 && clean !== lastCepSearched.value) {
      fetchViaCep(clean);
    }
  }
);

watch(
  () => form.value.cpfcnpj,
  () => validateDocumentField(false)
);

watch(
  () => props.context,
  () => {
    fillFromContext();
  },
  { immediate: true }
);

onMounted(() => {
  loadAux();
  fillFromContext();
});
</script>

<template>
  <div class="h-full flex flex-col">
    <div class="flex gap-6 p-4 overflow-hidden flex-1">
      <!-- Coluna de passos -->
      <div class="w-64 shrink-0 space-y-4 sticky top-0 self-start">
        <div class="flex items-center justify-between">
          <div class="text-sm font-semibold text-n-slate-12">
            {{ $t('SGP.TABS.PRE_REGISTRATION') }}
          </div>
          <div
            class="px-2 py-1 rounded border text-[11px]"
            :class="
              type
                ? 'border-n-brand text-n-brand'
                : 'border-n-weak text-n-slate-11'
            "
          >
            {{
              type
                ? `${$t('SGP.PRE.STEP_TYPE')}: ${type === 'PF' ? $t('SGP.PRE.TYPE_PF') : $t('SGP.PRE.TYPE_PJ')}`
                : $t('SGP.PRE.STEP_TYPE_DESC')
            }}
          </div>
        </div>
        <div class="text-xs text-n-slate-11">
          {{ $t('SGP.PRE.REQUIRED_HINT') }}
        </div>
        <div class="space-y-2">
          <div
            v-for="(item, index) in steps"
            :key="item.key"
            class="flex items-start gap-3 rounded-md px-3 py-2 cursor-pointer"
            :class="{
              'bg-n-surface-1 ring-1 ring-n-brand/60': step === index,
              'bg-n-surface-1/40 border border-n-weak/70': step !== index,
            }"
            @click="index <= step ? (step = index) : null"
          >
            <div
              class="h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold"
              :class="[
                stepStatus(index) === 'current'
                  ? 'bg-n-brand text-white'
                  : stepStatus(index) === 'done'
                    ? 'bg-n-teal-9 text-white'
                    : 'bg-n-slate-4 text-n-slate-12',
              ]"
            >
              {{ index + 1 }}
            </div>
            <div class="flex-1">
              <div class="text-sm font-medium text-n-slate-12">
                {{ item.title }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{ item.subtitle }}
              </div>
            </div>
            <div
              v-if="stepStatus(index) === 'done'"
              class="text-n-teal-10 text-xs font-semibold"
            >
              {{ $t('SGP.SYMBOLS.CHECK') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Conteúdo -->
      <div class="flex-1 h-full overflow-y-auto pr-1 space-y-4">
        <div
          class="border border-n-weak rounded-lg bg-n-surface-1 p-4 space-y-4"
        >
          <div
            v-if="Object.keys(errors).length"
            class="border border-n-ruby-6 bg-n-ruby-2/40 text-n-ruby-11 text-sm rounded-md p-3"
          >
            <div class="font-medium mb-1">
              {{ $t('SGP.PRE.VALIDATION_TITLE') }}
            </div>
            <ul class="list-disc list-inside space-y-0.5">
              <li v-for="(msg, key) in errors" :key="key">
                {{ msg }}
              </li>
            </ul>
          </div>

          <!-- Etapa 1 - Tipo de cliente -->
          <div v-show="step === 0" class="space-y-4">
            <div class="space-y-1">
              <div class="text-sm font-semibold text-n-slate-12">
                {{ $t('SGP.PRE.STEP_TYPE') }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{ $t('SGP.PRE.STEP_TYPE_DESC') }}
              </div>
              <div v-if="errors.type" class="text-xs text-n-ruby-11">
                {{ errors.type }}
              </div>
            </div>
            <div class="grid md:grid-cols-2 gap-3">
              <button
                type="button"
                class="border rounded-lg p-4 text-left transition-all h-full bg-n-surface-1"
                :class="[
                  type === 'PF'
                    ? 'border-n-brand ring-1 ring-n-brand shadow-sm'
                    : 'border-n-weak hover:border-n-slate-8',
                ]"
                @click="setTypeSelection('PF')"
              >
                <div class="text-sm font-semibold text-n-slate-12">
                  {{ $t('SGP.PRE.TYPE_PF') }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ $t('SGP.PRE.TYPE_PF_DESC') }}
                </div>
              </button>
              <button
                type="button"
                class="border rounded-lg p-4 text-left transition-all h-full bg-n-surface-1"
                :class="[
                  type === 'PJ'
                    ? 'border-n-brand ring-1 ring-n-brand shadow-sm'
                    : 'border-n-weak hover:border-n-slate-8',
                ]"
                @click="setTypeSelection('PJ')"
              >
                <div class="text-sm font-semibold text-n-slate-12">
                  {{ $t('SGP.PRE.TYPE_PJ') }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ $t('SGP.PRE.TYPE_PJ_DESC') }}
                </div>
              </button>
            </div>
          </div>

          <!-- Etapa 2 - Cliente -->
          <div v-show="step === 1" class="space-y-3">
            <div class="grid md:grid-cols-2 gap-3">
              <Input
                v-model="form.nome"
                :label="`${isPF ? $t('SGP.FORMS.NAME') : $t('SGP.PRE.BUSINESS_NAME')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
                :message="errors.nome"
                message-type="error"
                @update:model-value="clearError('nome')"
              />
              <Input
                v-if="isPJ"
                v-model="form.nomefantasia"
                :label="$t('SGP.FORMS.TRADE_NAME')"
              />
              <Input
                v-model="form.cpfcnpj"
                :label="`${isPF ? $t('SGP.FORMS.CPF') : $t('SGP.FORMS.CNPJ')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
                :placeholder="
                  isPF
                    ? $t('SGP.FORMS.CPF_PLACEHOLDER')
                    : $t('SGP.FORMS.CNPJ_PLACEHOLDER')
                "
                :message="errors.cpfcnpj"
                message-type="error"
                @update:model-value="clearError('cpfcnpj')"
                @blur="validateDocumentField(true)"
              />
              <Input
                v-model="form.email"
                :label="`${$t('SGP.FORMS.EMAIL')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
                :message="errors.email"
                message-type="error"
                @update:model-value="clearError('email')"
              />
              <Input
                v-model="form.celular"
                :label="`${$t('SGP.FORMS.PHONE')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
                :placeholder="$t('SGP.FORMS.PHONE_PLACEHOLDER')"
                :message="errors.celular"
                message-type="error"
                @update:model-value="clearError('celular')"
              />
              <Input
                v-if="isPJ"
                v-model="form.respempresa"
                :label="$t('SGP.FORMS.COMPANY_RESPONSIBLE')"
              />
              <Input
                v-if="isPJ"
                v-model="form.respcpf"
                :label="$t('SGP.FORMS.COMPANY_RESP_CPF')"
              />
              <Input
                v-if="isPF"
                v-model="form.datanasc"
                type="date"
                :label="$t('SGP.PRE.BIRTHDATE')"
              />
            </div>

            <div class="flex items-center gap-2">
              <input
                id="toggle-advanced"
                v-model="showAdvanced"
                type="checkbox"
                class="rounded border-n-weak"
              />
              <label for="toggle-advanced" class="text-sm text-n-slate-12">
                {{ $t('SGP.PRE.SHOW_ADVANCED') }}
              </label>
            </div>

            <div
              v-if="showAdvanced && isPF"
              class="space-y-3 border border-n-weak rounded-md p-3 bg-n-solid-2/30"
            >
              <div class="text-sm font-semibold text-n-slate-12">
                {{ $t('SGP.PRE.PERSONAL_EXTRA') }}
              </div>
              <div class="grid md:grid-cols-2 gap-3">
                <Input v-model="form.rg" :label="$t('SGP.PRE.RG')" />
                <Input
                  v-model="form.rg_emissor"
                  :label="$t('SGP.PRE.RG_EMITTER')"
                />
                <Input
                  v-model="form.nomepai"
                  :label="$t('SGP.PRE.FATHER_NAME')"
                />
                <Input
                  v-model="form.nomemae"
                  :label="$t('SGP.PRE.MOTHER_NAME')"
                />
                <Input
                  v-model="form.nacionalidade"
                  :label="$t('SGP.PRE.NATIONALITY')"
                />
                <Input
                  v-model="form.naturalidade"
                  :label="$t('SGP.PRE.BIRTH_PLACE')"
                />
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ $t('SGP.PRE.MARITAL_STATUS') }}
                  </label>
                  <select
                    v-model="form.estadocivil"
                    class="!h-10 !px-3 !py-2.5 !rounded !bg-n-surface-1 !text-sm text-n-slate-12 !outline-n-weak hover:!outline-n-slate-6 focus:!outline-n-slate-7 !mb-0"
                  >
                    <option value="">{{ $t('SGP.PRE.OPTIONAL_FIELD') }}</option>
                    <option value="S">{{ $t('SGP.PRE.STATUS_SINGLE') }}</option>
                    <option value="C">
                      {{ $t('SGP.PRE.STATUS_MARRIED') }}
                    </option>
                    <option value="D">
                      {{ $t('SGP.PRE.STATUS_DIVORCED') }}
                    </option>
                    <option value="V">
                      {{ $t('SGP.PRE.STATUS_WIDOWED') }}
                    </option>
                  </select>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ $t('SGP.PRE.GENDER') }}
                  </label>
                  <select
                    v-model="form.sexo"
                    class="!h-10 !px-3 !py-2.5 !rounded !bg-n-surface-1 !text-sm text-n-slate-12 !outline-n-weak hover:!outline-n-slate-6 focus:!outline-n-slate-7 !mb-0"
                  >
                    <option value="">{{ $t('SGP.PRE.OPTIONAL_FIELD') }}</option>
                    <option value="M">{{ $t('SGP.PRE.GENDER_M') }}</option>
                    <option value="F">{{ $t('SGP.PRE.GENDER_F') }}</option>
                  </select>
                </div>
                <Input
                  v-model="form.profissao"
                  :label="$t('SGP.PRE.PROFESSION')"
                />
                <Input v-model="form.observacao" :label="$t('SGP.PRE.OBS')" />
              </div>
            </div>
          </div>

          <!-- Etapa 3 - Endereço -->
          <div v-show="step === 2" class="space-y-3">
            <div class="grid md:grid-cols-2 gap-3">
              <Input
                v-model="form.cep"
                :label="`${$t('SGP.FORMS.CEP')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
                placeholder="00000-000"
                :message="errors.cep"
                message-type="error"
                @blur="() => fetchViaCep((form.cep || '').replace(/\D+/g, ''))"
                @update:model-value="clearError('cep')"
              />
              <div class="text-xs text-n-slate-11 flex items-center">
                <span v-if="cepError" class="text-n-ruby-11">{{
                  cepError
                }}</span>
                <span v-else-if="loadingCep">{{ $t('SGP.LOADING') }}</span>
                <span v-else>{{ $t('SGP.PRE.CEP_HINT') }}</span>
              </div>
            </div>

            <div class="grid md:grid-cols-2 gap-3">
              <div class="relative">
                <Input
                  v-model="form.logradouro"
                  :label="`${$t('SGP.FORMS.ADDRESS')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
                  :message="errors.logradouro"
                  message-type="error"
                  @update:model-value="clearError('logradouro')"
                  @focus="handleAddressFocus"
                  @blur="handleAddressBlur"
                />
                <ul
                  v-if="addressAutocompleteOpen"
                  class="absolute z-50 mt-1 w-full max-h-52 overflow-auto rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container shadow-lg text-sm"
                >
                  <template v-if="addressAutocompleteLoading">
                    <li class="px-3 py-2 text-n-slate-11">
                      {{ $t('SGP.LOADING') }}
                    </li>
                  </template>
                  <template v-else>
                    <li
                      v-for="item in addressAutocompleteResults"
                      :key="`${item.cep}-${item.logradouro}-${item.bairro}`"
                      class="cursor-pointer px-3 py-2 hover:bg-n-alpha-2"
                      @mousedown.prevent="selectAddressSuggestion(item)"
                    >
                      <div class="text-n-slate-12">
                        {{ item.logradouro }}
                      </div>
                      <div class="text-xs text-n-slate-11">
                        {{ item.bairro }}
                        {{ $t('SGP.SYMBOLS.BULLET') }}
                        {{ item.localidade }}{{ $t('SGP.SYMBOLS.SLASH')
                        }}{{ item.uf }}
                        {{ $t('SGP.SYMBOLS.BULLET') }}
                        {{ $t('SGP.FORMS.CEP') }} {{ item.cep }}
                      </div>
                    </li>
                  </template>
                </ul>
              </div>
              <Input
                ref="numberInputRef"
                v-model="form.numero"
                :label="`${$t('SGP.FORMS.NUMBER')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
                :message="errors.numero"
                message-type="error"
                @update:model-value="clearError('numero')"
              />
              <Input
                v-model="form.bairro"
                :label="`${$t('SGP.FORMS.DISTRICT')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
                :message="errors.bairro"
                message-type="error"
                @update:model-value="clearError('bairro')"
              />
              <Input
                v-model="form.cidade"
                :label="`${$t('SGP.FORMS.CITY')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
                :message="errors.cidade"
                message-type="error"
                @update:model-value="clearError('cidade')"
              />
              <Input
                v-model="form.uf"
                :label="`${$t('SGP.FORMS.STATE')} ${$t('SGP.SYMBOLS.ASTERISK')}`"
                :message="errors.uf"
                message-type="error"
                @update:model-value="clearError('uf')"
              />
              <Input
                v-model="form.complemento"
                :label="$t('SGP.PRE.COMPLEMENT')"
              />
              <Input
                v-model="form.pontoreferencia"
                :label="$t('SGP.PRE.REFERENCE_POINT')"
              />
              <Input
                v-model="form.condominio"
                :label="$t('SGP.PRE.CONDOMINIUM')"
              />
            </div>

            <div class="grid md:grid-cols-2 gap-3">
              <Input
                v-model="form.map_ll"
                :label="$t('SGP.PRE.LATLNG')"
                :message="errors.map_ll"
                message-type="error"
                @update:model-value="clearError('map_ll')"
              />
              <div class="flex items-center gap-3">
                <Button
                  variant="outline"
                  color="slate"
                  size="sm"
                  :disabled="!canManualGeocode || geocodeLoading"
                  :is-loading="geocodeLoading"
                  @click="geocodeFromAddress"
                >
                  {{ $t('SGP.PRE.GEOLOCATE') }}
                </Button>
                <div class="text-xs">
                  <span v-if="geocodeError" class="text-n-ruby-11">
                    {{ geocodeError }}
                  </span>
                  <span v-else class="text-n-slate-11">
                    {{ $t('SGP.PRE.GEO_HINT') }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Etapa 4 - Plano / contrato -->
          <div v-show="step === 3" class="space-y-3">
            <div class="grid md:grid-cols-3 gap-3">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('SGP.PRE.PLAN') }} {{ $t('SGP.SYMBOLS.ASTERISK') }}
                </label>
                <div v-on-clickaway="() => (planOpen = false)" class="relative">
                  <button
                    type="button"
                    class="h-10 w-full px-3 py-2.5 rounded border text-sm text-left bg-n-surface-1 focus:outline-none focus:ring-1 focus:ring-n-slate-7 focus:border-n-slate-7"
                    :class="{
                      'border-n-ruby-8': errors.plano_id,
                      'border-n-weak': !errors.plano_id,
                      'text-n-slate-11': !form.plano_id,
                      'text-n-slate-12': form.plano_id,
                    }"
                    @click="planOpen = !planOpen"
                  >
                    {{ selectedPlanLabel }}
                  </button>
                  <ul
                    v-if="planOpen"
                    class="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container shadow-lg text-sm"
                  >
                    <li
                      class="cursor-pointer px-3 py-2 hover:bg-n-alpha-2 text-n-slate-11"
                      @mousedown.prevent="
                        () => {
                          form.plano_id = '';
                          planOpen = false;
                          clearError('plano_id');
                        }
                      "
                    >
                      {{ $t('SGP.PRE.SELECT_OPTION') }}
                    </li>
                    <li
                      v-for="plan in plans"
                      :key="plan.id"
                      class="cursor-pointer px-3 py-2 hover:bg-n-alpha-2 text-n-slate-12"
                      @mousedown.prevent="
                        () => {
                          form.plano_id = plan.id;
                          planOpen = false;
                          clearError('plano_id');
                        }
                      "
                    >
                      {{ plan.descricao }}
                    </li>
                  </ul>
                </div>
                <span v-if="errors.plano_id" class="text-xs text-n-ruby-11">
                  {{ errors.plano_id }}
                </span>
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('SGP.PRE.DUE_DATE') }} {{ $t('SGP.SYMBOLS.ASTERISK') }}
                </label>
                <div v-on-clickaway="() => (dueOpen = false)" class="relative">
                  <button
                    type="button"
                    class="h-10 w-full px-3 py-2.5 rounded border text-sm text-left bg-n-surface-1 focus:outline-none focus:ring-1 focus:ring-n-slate-7 focus:border-n-slate-7"
                    :class="{
                      'border-n-ruby-8': errors.vencimento_id,
                      'border-n-weak': !errors.vencimento_id,
                      'text-n-slate-11': !form.vencimento_id,
                      'text-n-slate-12': form.vencimento_id,
                    }"
                    @click="dueOpen = !dueOpen"
                  >
                    {{ selectedDueLabel }}
                  </button>
                  <ul
                    v-if="dueOpen"
                    class="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container shadow-lg text-sm"
                  >
                    <li
                      class="cursor-pointer px-3 py-2 hover:bg-n-alpha-2 text-n-slate-11"
                      @mousedown.prevent="
                        () => {
                          form.vencimento_id = '';
                          dueOpen = false;
                          clearError('vencimento_id');
                        }
                      "
                    >
                      {{ $t('SGP.PRE.SELECT_OPTION') }}
                    </li>
                    <li
                      v-for="due in dueDates"
                      :key="due.id"
                      class="cursor-pointer px-3 py-2 hover:bg-n-alpha-2 text-n-slate-12"
                      @mousedown.prevent="
                        () => {
                          form.vencimento_id = due.id;
                          dueOpen = false;
                          clearError('vencimento_id');
                        }
                      "
                    >
                      {{ due.vencimento || due.dia || due.id }}
                    </li>
                  </ul>
                </div>
                <span
                  v-if="errors.vencimento_id"
                  class="text-xs text-n-ruby-11"
                >
                  {{ errors.vencimento_id }}
                </span>
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('SGP.PRE.VENDOR') }}
                </label>
                <div
                  v-on-clickaway="() => (vendorOpen = false)"
                  class="relative"
                >
                  <button
                    type="button"
                    class="h-10 w-full px-3 py-2.5 rounded border border-n-weak text-sm text-left bg-n-surface-1 focus:outline-none focus:ring-1 focus:ring-n-slate-7 focus:border-n-slate-7"
                    :class="{
                      'text-n-slate-11': !form.vendedor_id,
                      'text-n-slate-12': form.vendedor_id,
                    }"
                    @click="vendorOpen = !vendorOpen"
                  >
                    {{ selectedVendorLabel }}
                  </button>
                  <ul
                    v-if="vendorOpen"
                    class="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container shadow-lg text-sm"
                  >
                    <li
                      class="cursor-pointer px-3 py-2 hover:bg-n-alpha-2 text-n-slate-11"
                      @mousedown.prevent="
                        () => {
                          form.vendedor_id = '';
                          vendorOpen = false;
                        }
                      "
                    >
                      {{ $t('SGP.PRE.SELECT_OPTION') }}
                    </li>
                    <li
                      v-for="vendor in vendors"
                      :key="vendor.id"
                      class="cursor-pointer px-3 py-2 hover:bg-n-alpha-2 text-n-slate-12"
                      @mousedown.prevent="
                        () => {
                          form.vendedor_id = vendor.id;
                          vendorOpen = false;
                        }
                      "
                    >
                      {{ vendor.nome }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <input
                id="toggle-contract"
                v-model="showContract"
                type="checkbox"
                class="rounded border-n-weak"
              />
              <label for="toggle-contract" class="text-sm text-n-slate-12">
                {{ $t('SGP.PRE.SHOW_CONTRACT') }}
              </label>
            </div>

            <div
              v-if="showContract"
              class="space-y-3 border border-n-weak rounded-md p-3 bg-n-solid-2/30"
            >
              <div class="grid md:grid-cols-3 gap-3">
                <Input
                  v-model="form.planointernet_id"
                  :label="$t('SGP.PRE.PLAN_INTERNET_ID')"
                />
                <Input
                  v-model="form.planobase_id"
                  :label="$t('SGP.PRE.PLAN_BASE_ID')"
                />
                <Input
                  v-model="form.portador_id"
                  :label="$t('SGP.PRE.PORTADOR_ID')"
                />
                <Input v-model="form.pop_id" :label="$t('SGP.PRE.POP_ID')" />
                <Input v-model="form.nas_id" :label="$t('SGP.PRE.NAS_ID')" />
                <Input v-model="form.login" :label="$t('SGP.PRE.LOGIN')" />
                <Input
                  v-model="form.senha"
                  :label="$t('SGP.PRE.PASSWORD')"
                  type="password"
                />
                <Input
                  v-model="form.central_senha"
                  :label="$t('SGP.PRE.CENTRAL_PASSWORD')"
                  type="password"
                />
                <Input
                  v-model="form.fidelidade_id"
                  :label="$t('SGP.PRE.FIDELIDADE_ID')"
                />
                <Input
                  v-model="form.contrato_id"
                  :label="$t('SGP.PRE.CONTRATO_ID')"
                />
                <Input
                  v-model="form.modoaquisicao"
                  :label="$t('SGP.PRE.MODO_AQUISICAO')"
                />
                <Input
                  v-model="form.servicodesc"
                  :label="$t('SGP.PRE.SERVICE_DESC')"
                />
              </div>
              <div class="grid md:grid-cols-3 gap-3">
                <Input v-model="form.ip" :label="$t('SGP.PRE.IP')" />
                <Input v-model="form.mac" :label="$t('SGP.PRE.MAC')" />
                <Input
                  v-model="form.ippool_id"
                  :label="$t('SGP.PRE.IPPOOL_ID')"
                />
                <Input
                  v-model="form.mac_dhcp"
                  :label="$t('SGP.PRE.MAC_DHCP')"
                />
                <Input
                  v-model="form.splitter_id"
                  :label="$t('SGP.PRE.SPLITTER_ID')"
                />
                <Input
                  v-model="form.splitter_port"
                  :label="$t('SGP.PRE.SPLITTER_PORT')"
                />
                <Input
                  v-model="form.tipo_equipamento_id"
                  :label="$t('SGP.PRE.EQUIPMENT_TYPE_ID')"
                />
                <Input
                  v-model="form.midia_id"
                  :label="$t('SGP.PRE.MIDIA_ID')"
                />
                <Input
                  v-model="form.tecnico_id"
                  :label="$t('SGP.PRE.TECHNICIAN_ID')"
                />
                <Input
                  v-model="form.os_instalacao"
                  :label="$t('SGP.PRE.OS_INSTALL')"
                />
              </div>
              <div class="grid md:grid-cols-3 gap-3">
                <Input
                  v-model="form.instalacao_quantidade_parcelas"
                  :label="$t('SGP.PRE.INSTALLMENTS_QTY')"
                />
                <Input
                  v-model="form.instalacao_preco"
                  :label="$t('SGP.PRE.INSTALL_PRICE')"
                />
                <Input
                  v-model="form.instalacao_desconto"
                  :label="$t('SGP.PRE.INSTALL_DISCOUNT')"
                />
                <Input
                  v-model="form.instalacao_entrada"
                  :label="$t('SGP.PRE.INSTALL_ENTRY')"
                />
                <Input
                  v-model="form.instalacao_entrada_forma"
                  :label="$t('SGP.PRE.INSTALL_ENTRY_METHOD')"
                />
                <Input
                  v-model="form.instalacao_parcela_forma"
                  :label="$t('SGP.PRE.INSTALLMENT_METHOD')"
                />
              </div>
              <div class="grid md:grid-cols-3 gap-3">
                <Input
                  v-model="form.comissao_tipo"
                  :label="$t('SGP.PRE.COMMISSION_TYPE')"
                />
                <Input
                  v-model="form.comissao_valor"
                  :label="$t('SGP.PRE.COMMISSION_VALUE')"
                />
                <Input
                  v-model="form.comissao_qtd_parcelas"
                  :label="$t('SGP.PRE.COMMISSION_QTY')"
                />
                <Input
                  v-model="form.usuariocad_id"
                  :label="$t('SGP.PRE.USERCAD_ID')"
                />
                <Input
                  v-model="form.formacobranca_id"
                  :label="$t('SGP.PRE.BILLING_FORM_ID')"
                />
              </div>
              <div class="flex items-center gap-2">
                <input
                  id="precadastro_ativar"
                  v-model="form.precadastro_ativar"
                  type="checkbox"
                  class="rounded border-n-weak"
                />
                <label for="precadastro_ativar" class="text-sm text-n-slate-12">
                  {{ $t('SGP.PRE.ACTIVATE_NOW') }}
                </label>
              </div>
            </div>
          </div>

          <!-- Etapa 5 - Revisão e envio -->
          <div v-show="step === 4" class="space-y-3">
            <div class="text-sm font-semibold text-n-slate-12">
              {{ $t('SGP.PRE.SUMMARY_TITLE') }}
            </div>
            <div class="grid md:grid-cols-2 gap-3">
              <div
                class="border border-n-weak rounded-md p-3 space-y-1 bg-n-surface-1"
              >
                <div class="text-xs uppercase text-n-slate-11">
                  {{ $t('SGP.CUSTOMER.CONTRACTS') || $t('SGP.FORMS.NAME') }}
                </div>
                <div class="text-sm font-medium text-n-slate-12">
                  {{ form.nome }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{
                    type === 'PF'
                      ? $t('SGP.PRE.TYPE_PF')
                      : type === 'PJ'
                        ? $t('SGP.PRE.TYPE_PJ')
                        : '—'
                  }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ form.email }} {{ $t('SGP.SYMBOLS.BULLET') }}
                  {{ form.celular }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ form.cpfcnpj }}
                </div>
              </div>
              <div
                class="border border-n-weak rounded-md p-3 space-y-1 bg-n-surface-1"
              >
                <div class="text-xs uppercase text-n-slate-11">
                  {{ $t('SGP.PRE.ADDRESS_SECTION') }}
                </div>
                <div class="text-sm font-medium text-n-slate-12">
                  {{ form.logradouro }}{{ $t('SGP.SYMBOLS.COMMA') }}
                  {{ form.numero }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ form.bairro }} {{ $t('SGP.SYMBOLS.DASH') }}
                  {{ form.cidade }} {{ $t('SGP.SYMBOLS.SLASH') }} {{ form.uf }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ $t('SGP.FORMS.CEP') }} {{ form.cep }}
                  {{ $t('SGP.SYMBOLS.PIPE') }} {{ $t('SGP.PRE.LATLNG')
                  }}{{ $t('SGP.SYMBOLS.COLON') }} {{ form.map_ll }}
                </div>
              </div>
              <div
                class="border border-n-weak rounded-md p-3 space-y-1 bg-n-surface-1"
              >
                <div class="text-xs uppercase text-n-slate-11">
                  {{ $t('SGP.PRE.CONTRACT_SECTION') }}
                </div>
                <div class="text-sm font-medium text-n-slate-12">
                  {{ $t('SGP.PRE.PLAN') }}{{ $t('SGP.SYMBOLS.COLON') }}
                  {{ form.plano_id || $t('SGP.SYMBOLS.DASH') }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ $t('SGP.PRE.DUE_DATE') }}{{ $t('SGP.SYMBOLS.COLON') }}
                  {{ form.vencimento_id || $t('SGP.SYMBOLS.DASH') }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ $t('SGP.PRE.VENDOR') }}{{ $t('SGP.SYMBOLS.COLON') }}
                  {{ form.vendedor_id || $t('SGP.SYMBOLS.DASH') }}
                </div>
              </div>
              <div
                v-if="form.splitter_id || form.splitter_port"
                class="border border-n-weak rounded-md p-3 space-y-1 bg-n-surface-1"
              >
                <div class="text-xs uppercase text-n-slate-11">
                  {{ $t('SGP.PRE.CTO_LABEL') }} {{ $t('SGP.SYMBOLS.SLASH') }}
                  {{ $t('SGP.PRE.SPLITTER_ID') }}
                </div>
                <div class="text-sm font-medium text-n-slate-12">
                  {{ form.splitter_id || $t('SGP.SYMBOLS.DASH') }}
                  {{ $t('SGP.SYMBOLS.DASH') }}
                  {{ form.splitter_port || $t('SGP.SYMBOLS.DASH') }}
                </div>
                <div v-if="form.map_ll" class="text-xs text-n-slate-11">
                  {{ form.map_ll }}
                </div>
              </div>
            </div>

            <div v-if="props.context?.result" class="space-y-2">
              <div class="text-xs text-n-slate-11">
                {{
                  $t('SGP.RESULTS.CTO_FOUND_COUNT', {
                    count:
                      (props.context.result?.[0]?.SGPMaps || []).length || 0,
                  })
                }}
              </div>
              <div class="grid md:grid-cols-2 gap-2">
                <div
                  v-for="(cto, idx) in props.context.result?.[0]?.SGPMaps || []"
                  :key="idx"
                  class="border border-n-weak rounded-md p-3 bg-n-surface-1 cursor-pointer"
                  :class="{
                    'ring-2 ring-n-brand': selectedCtoIndex === idx,
                  }"
                  @click="selectCto(cto, idx)"
                >
                  <div class="text-sm font-medium text-n-slate-12">
                    {{ cto.nome }}
                  </div>
                  <div class="text-xs text-n-slate-11">
                    {{ $t('CONVERSATION.DIALOGS.LOCATION_CHECK.DISTANCE') }}
                    {{ cto.distancia?.toFixed?.(0) || cto.distancia }}
                    {{ $t('CONVERSATION.DIALOGS.LOCATION_CHECK.METERS') }}
                  </div>
                  <div class="text-xs text-n-slate-11">
                    {{ $t('SGP.PRE.LATLNG') }}{{ $t('SGP.SYMBOLS.COLON') }}
                    {{ cto.lat }}{{ $t('SGP.SYMBOLS.COMMA') }} {{ cto.lng }}
                  </div>
                  <div
                    v-if="cto.splitter_id || cto.splitter_port"
                    class="text-xs text-n-slate-11"
                  >
                    {{ $t('SGP.PRE.SPLITTER_ID') }}{{ $t('SGP.SYMBOLS.COLON') }}
                    {{ cto.splitter_id || $t('SGP.SYMBOLS.DASH') }}
                    {{ $t('SGP.SYMBOLS.DASH') }}
                    {{ $t('SGP.PRE.SPLITTER_PORT')
                    }}{{ $t('SGP.SYMBOLS.COLON') }}
                    {{
                      cto.splitter_port ||
                      cto.port ||
                      cto.porta ||
                      $t('SGP.SYMBOLS.DASH')
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Rodapé fixo -->
    <div
      class="bg-n-surface-1 border-t border-n-weak px-4 py-3 flex items-center justify-between gap-3"
    >
      <div class="flex flex-col text-xs text-n-slate-11 min-w-[220px]">
        <span v-if="submitResult && !submitResult.error" class="text-n-teal-11">
          {{
            $t('SGP.PRE.SUCCESS', {
              id: submitResult.precadastro_id || submitResult.id || '',
            })
          }}
        </span>
        <span
          v-else-if="submitResult && submitResult.error"
          class="text-n-ruby-11"
        >
          {{ submitResult.message || $t('SGP.RESULTS.ERROR_GENERIC') }}
        </span>
        <span v-else>{{ $t('SGP.PRE.REQUIRED_HINT') }}</span>
      </div>
      <div class="flex items-center gap-2">
        <Button variant="outline" :disabled="submitting" @click="emit('close')">
          {{ $t('SGP.PRE.CANCEL') }}
        </Button>
        <Button variant="ghost" :disabled="step === 0" @click="goPrev">
          {{ $t('SGP.PRE.BACK') }}
        </Button>
        <Button
          v-if="step < steps.length - 1"
          :disabled="submitting"
          :loading="submitting"
          color="blue"
          @click="goNext"
        >
          {{ $t('SGP.PRE.NEXT') }}
        </Button>
        <Button
          v-else
          :disabled="submitting"
          :loading="submitting"
          color="blue"
          @click="submit"
        >
          {{ $t('SGP.PRE.CONFIRM') }}
        </Button>
      </div>
    </div>
  </div>
</template>
